import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { EditDxCodeData } from "../../../../../components/modals/editDxCodeSidePanel/editDxCodeSidePanel";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { DxCodeConfigTab } from "../../../../../enums/dxCodeConfigTab";
import { updateNccnIcdConfig } from "../../../../../redux/slice/nccnIcdConfigSlice";
import { updateNccnRegimenConfig } from "../../../../../redux/slice/nccnRegimenConfigSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { logError } from "../../../../../utils/customLogger";
import {
  buildDrugCodesForPersist,
  buildIcdCodesForPersist,
  createPriorityConfig,
} from "../utils/tableDataTransformers";

interface UseEditDxCodeSaveParams {
  activeTab: DxCodeConfigTab;
  setIsSaving: (isSaving: boolean) => void;
  onClose: () => void;
}

/**
 * Hook to handle saving edited DX code configurations
 */
export const useEditDxCodeSave = ({
  activeTab,
  setIsSaving,
  onClose,
}: UseEditDxCodeSaveParams) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleEditDxCodeSave = useCallback(
    async (data: EditDxCodeData) => {
      setIsSaving(true);
      try {
        const updates: any = {};

        if (activeTab === DxCodeConfigTab.DRUG) {
          // Per-org drug config: persist the engine's shape — a `codes` array
          // carrying each code's `rank` (0 = pin, >0 = priority, absent =
          // unranked) — plus the drugType as `category`. The engine reads only
          // this doc, so we write only these fields (no icd_codes / overlay).
          updates.codes = buildDrugCodesForPersist(data.dxCodes);
          if (data.drugType) {
            updates.category = data.drugType;
          }
          await dispatch(
            updateNccnIcdConfig(data.selectedOrgId, data.id, updates),
          );
        } else {
          // Regimen config stays global: `icdcodes` + per-org priority overlay.
          const icdcodes = buildIcdCodesForPersist(data.dxCodes);
          updates.icdcodes = icdcodes;
          updates[`priority_config_org_specific.${data.selectedOrgId}`] =
            createPriorityConfig(data.dxCodes);
          await dispatch(updateNccnRegimenConfig(data.id, updates));
        }

        toast.success(ToastMessages.DX_CODE_CONFIG_SAVE_SUCCESS);
        onClose();
      } catch (error) {
        logError(error as Error, "useEditDxCodeSave - handleEditDxCodeSave");
        toast.error(ToastMessages.DX_CODE_CONFIG_SAVE_ERROR);
      } finally {
        setIsSaving(false);
      }
    },
    [activeTab, dispatch, setIsSaving, onClose],
  );

  return handleEditDxCodeSave;
};
